import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        brand: 'bg-brand text-brand-foreground hover:bg-brand-hover',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        transparent: 'text-foreground bg-transparent hover:bg-accent',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/90',
        tertiary: 'bg-muted text-muted-foreground hover:bg-muted/80',
        tetriary: 'bg-muted text-muted-foreground hover:bg-muted/80',
        ghost:
          'bg-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        link: 'bg-transparent text-brand underline-offset-4 hover:text-brand-hover hover:underline',
        gradient:
          'bg-default-gradient from-gradient-start to-gradient-end font-semibold text-white/80 transition-colors duration-200 hover:text-white',
        affirmative:
          'bg-contact-light-secondary text-primary-foreground hover:bg-contact-light-tertiary dark:bg-contact-dark-secondary dark:text-primary-foreground dark:hover:bg-contact-dark-tertiary',
        neutral:
          'bg-settings-light-secondary text-primary-foreground hover:bg-settings-light-tertiary dark:bg-settings-dark-secondary dark:text-foreground dark:hover:bg-settings-dark-tertiary',
      },
      size: {
        default: 'h-9 px-4 py-2 gap-2',
        sm: 'h-8 rounded-full px-3 text-xs',
        lg: 'h-10 rounded-full px-8',
        icon: 'h-9 w-9 p-0 flex items-center justify-center',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, type, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        type={asChild ? undefined : (type ?? 'button')}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
